import UIComponent from "../ui/component.js";
import ExtractApi from "../api/extract.js";

const TERMINAL_STATUSES = ['completed', 'failed'];
const POLL_INTERVAL_MS = 1500;

export class ExtractForm extends UIComponent {
    init() {
        this.statusLabels = JSON.parse(this.element.dataset.statusLabels || '{}');
        this.resultEmptyLabel = this.element.dataset.resultEmpty || '';
        this.copyLabel = this.element.dataset.copyLabel || 'Копировать';
        this.copyDoneLabel = this.element.dataset.copyDoneLabel || 'Скопировано';
        this.downloadLabel = this.element.dataset.downloadLabel || 'Скачать';
        this.downloadSelectFormatLabel = this.element.dataset.downloadSelectFormat || 'Выберите формат';
        this.downloadTxtLabel = this.element.dataset.downloadTxt || 'txt';
        this.downloadMdLabel = this.element.dataset.downloadMd || 'md';
        this.metaPagesLabel = this.element.dataset.metaPages || ':count стр.';
        this.metaDurationLabel = this.element.dataset.metaDuration || ':count сек';
        this.queueSelectedLabel = this.element.dataset.queueSelected || '';
        this.queueRemoveLabel = this.element.dataset.queueRemove || '';
        this.thinkToggleLabel = this.element.dataset.thinkToggleLabel || 'Показать разбор';
        this.thinkErrorLabel = this.element.dataset.thinkErrorLabel || 'Не удалось получить разбор';

        this.pendingFiles = [];
        this.isProcessing = false;

        this.includeComponents({
            dropzone: 'document@.extract-dropzone',
            browseBtn: 'document@[data-tbx-name="browseBtn"]',
            fileInput: 'document@[data-tbx-name="fileInput"]',
            languageInput: 'document@#extractLanguage',
            submitBtn: 'document@[data-tbx-name="submitBtn"]',
            queueInfo: 'document@[data-tbx-name="queueInfo"]',
            pendingFilesList: 'document@[data-tbx-name="pendingFilesList"]',
            resultsContainer: 'document@[data-tbx-name="resultsContainer"]',
        });

        this.browseBtn?.on('click', () => this.fileInput.element.click());
        this.fileInput?.element.addEventListener('change', (e) => this.onFilesSelected(e.target.files));
        this.submitBtn?.on('click', this.onSubmit.bind(this));
        this.pendingFilesList?.element?.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-pending-index]');
            if (!btn) {
                return;
            }
            this.removePendingFile(parseInt(btn.dataset.pendingIndex, 10));
        });

        this.resultsContainer?.element?.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-action]');
            if (!btn) {
                return;
            }

            const card = btn.closest('.card');
            if (!card) {
                return;
            }

            if (btn.dataset.action === 'copy') {
                this.copyResult(btn, card);
            }
        });

        this.resultsContainer?.element?.addEventListener('change', (e) => {
            const toggle = e.target.closest('[data-role="task-think-toggle"]');
            if (!toggle) {
                return;
            }

            const card = toggle.closest('.card');
            if (!card?._extractPayload) {
                return;
            }

            card._extractPayload.activeTask = toggle.checked ? 'think' : 'text';
            this.renderActiveTask(card);
        });

        const zone = this.dropzone?.element;
        if (zone) {
            zone.addEventListener('dragover', (e) => {
                e.preventDefault();
                zone.classList.add('border-primary', 'bg-light');
            });
            zone.addEventListener('dragleave', () => {
                zone.classList.remove('border-primary', 'bg-light');
            });
            zone.addEventListener('drop', (e) => {
                e.preventDefault();
                zone.classList.remove('border-primary', 'bg-light');
                this.onFilesSelected(e.dataTransfer?.files);
            });
        }
    }

    onFilesSelected(fileList) {
        if (!fileList?.length) {
            return;
        }

        this.pendingFiles.push(...Array.from(fileList));
        this.updateQueueInfo();
        this.submitBtn.element.disabled = this.pendingFiles.length === 0 || this.isProcessing;
    }

    updateQueueInfo() {
        if (!this.pendingFiles.length) {
            this.queueInfo?.addClass('d-none');
            if (this.pendingFilesList?.element) {
                this.pendingFilesList.element.innerHTML = '';
            }
            return;
        }

        this.queueInfo?.removeClass('d-none');
        this.renderPendingFiles();
    }

    renderPendingFiles() {
        const container = this.pendingFilesList?.element;
        if (!container) {
            return;
        }

        const selectedLabel = this.queueSelectedLabel.replace(':count', String(this.pendingFiles.length));
        const items = this.pendingFiles.map((file, index) => `
            <li class="list-group-item d-flex justify-content-between align-items-center px-0 gap-2">
                <span class="text-break">
                    ${this.escapeHtml(file.name)}
                    <span class="text-muted small ms-1">${this.escapeHtml(this.formatFileSize(file.size))}</span>
                </span>
                <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary flex-shrink-0"
                    data-pending-index="${index}"
                    aria-label="${this.escapeHtml(this.queueRemoveLabel)}"
                    title="${this.escapeHtml(this.queueRemoveLabel)}"
                >&times;</button>
            </li>
        `).join('');

        container.innerHTML = `
            <li class="list-group-item px-0 border-0 pb-1">
                <span class="text-muted small">${this.escapeHtml(selectedLabel)}</span>
            </li>
            ${items}
        `;
    }

    removePendingFile(index) {
        if (index < 0 || index >= this.pendingFiles.length) {
            return;
        }

        this.pendingFiles.splice(index, 1);
        if (this.fileInput?.element) {
            this.fileInput.element.value = '';
        }
        this.updateQueueInfo();
        this.submitBtn.element.disabled = this.pendingFiles.length === 0 || this.isProcessing;
    }

    formatFileSize(bytes) {
        if (bytes === 0) {
            return '0 Б';
        }

        const units = ['Б', 'КБ', 'МБ', 'ГБ'];
        const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
        const size = bytes / Math.pow(1024, unitIndex);

        return `${unitIndex === 0 ? size : size.toFixed(1)} ${units[unitIndex]}`;
    }

    async onSubmit(e) {
        e.preventDefault();
        if (!this.pendingFiles.length || this.isProcessing) {
            return;
        }

        this.setLoading(true);
        const files = [...this.pendingFiles];
        this.pendingFiles = [];
        this.updateQueueInfo();

        const language = this.languageInput?.element?.value || 'auto';

        for (const file of files) {
            await this.processFile(file, language);
        }

        this.setLoading(false);
        this.submitBtn.element.disabled = this.pendingFiles.length === 0;
    }

    async processFile(file, language) {
        const card = this.createResultCard(file.name);
        this.resultsContainer.element.prepend(card);

        const statusEl = card.querySelector('[data-role="status"]');

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('language', language);

            const data = await ExtractApi.store(formData);
            card.dataset.extractionId = String(data.id);
            this.setCardStatus(statusEl, data.status);

            const finalData = await this.pollUntilDone(data.id, statusEl);
            this.setCardStatus(statusEl, finalData.status);

            if (finalData.status === 'completed') {
                const [textOutcome, thinkOutcome] = await Promise.allSettled([
                    ExtractApi.result(data.id, 'text'),
                    ExtractApi.result(data.id, 'think'),
                ]);

                const textResult = textOutcome.status === 'fulfilled' ? textOutcome.value : null;
                const textError = textOutcome.status === 'rejected'
                    ? this.formatErrors(textOutcome.reason)
                    : null;
                const thinkResult = thinkOutcome.status === 'fulfilled' ? thinkOutcome.value : null;
                const thinkError = thinkOutcome.status === 'rejected'
                    ? this.formatErrors(thinkOutcome.reason)
                    : (finalData.tasks?.think?.error || null);

                if (!textResult && !thinkResult) {
                    this.renderResult(card, {
                        status: 'failed',
                        error: textError || thinkError || this.resultEmptyLabel,
                    });
                    return;
                }

                card._extractPayload = {
                    text: textResult,
                    think: thinkResult,
                    textError,
                    thinkError,
                    activeTask: 'text',
                    processingTime: finalData.processing_time,
                };

                const toggleWrap = card.querySelector('[data-role="task-think-wrap"]');
                if (thinkResult || thinkError) {
                    toggleWrap?.classList.remove('d-none');
                }

                this.renderActiveTask(card);
            } else {
                this.renderResult(card, finalData);
            }
        } catch (errors) {
            this.setCardStatus(statusEl, 'failed');
            this.renderResult(card, {
                status: 'failed',
                error: this.formatErrors(errors),
            });
        }
    }

    async pollUntilDone(id, statusEl) {
        let data = await ExtractApi.show(id);

        while (!TERMINAL_STATUSES.includes(data.status)) {
            await this.sleep(POLL_INTERVAL_MS);
            data = await ExtractApi.show(id);
            this.setCardStatus(statusEl, data.status);
        }

        return data;
    }

    createResultCard(fileName) {
        const thinkToggleId = `extract-think-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const card = document.createElement('div');
        card.className = 'card';
        card.dataset.fileName = fileName;
        card.innerHTML = `
            <div class="card-body text-start">
                <div class="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <h2 class="h6 mb-0 text-break">${this.escapeHtml(fileName)}</h2>
                    <span class="badge bg-secondary" data-role="status"></span>
                </div>
                <div data-role="meta" class="text-muted small mb-2 d-none"></div>
                <div data-role="actions" class="d-none d-flex align-items-center justify-content-between gap-2 mb-2 flex-wrap">
                    <div class="d-flex gap-2 flex-wrap">
                        <button type="button" class="btn btn-sm btn-outline-secondary" data-action="copy">
                            ${this.escapeHtml(this.copyLabel)}
                        </button>
                        <div class="dropdown-export extract-download">
                            <div class="dropdown">
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary dropdown-toggle"
                                    aria-expanded="false"
                                >
                                    ${this.escapeHtml(this.downloadLabel)}
                                </button>
                                <ul class="dropdown-menu dropdown-menu-end p-3 m-0">
                                    <li class="mb-2 text-dark small">
                                        ${this.escapeHtml(this.downloadSelectFormatLabel)}
                                    </li>
                                    <li>
                                        <div class="row g-2">
                                            <div class="col-6">
                                                <a
                                                    href="#"
                                                    data-role="download-txt"
                                                    class="btn btn-secondary btn-sm py-2 w-100 actions-export"
                                                >
                                                    <img src="/img/icons/export-txt.svg" class="me-2" alt="">
                                                    <span class="fs-6">${this.escapeHtml(this.downloadTxtLabel)}</span>
                                                </a>
                                            </div>
                                            <div class="col-6">
                                                <a
                                                    href="#"
                                                    data-role="download-md"
                                                    class="btn btn-secondary btn-sm py-2 w-100 actions-export"
                                                >
                                                    <span class="fs-6">${this.escapeHtml(this.downloadMdLabel)}</span>
                                                </a>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div class="d-none d-flex align-items-center gap-2" data-role="task-think-wrap">
                        <label class="form-check-label m-0" for="${thinkToggleId}">
                            ${this.escapeHtml(this.thinkToggleLabel)}
                        </label>
                        <div class="form-check form-switch m-0">
                            <input
                                type="checkbox"
                                data-role="task-think-toggle"
                                class="form-check-input"
                                id="${thinkToggleId}"
                            >
                        </div>
                    </div>
                </div>
                <div data-role="body">
                    <div class="d-flex align-items-center gap-2 text-muted">
                        <span class="spinner-border spinner-border-sm" role="status"></span>
                        <span>...</span>
                    </div>
                </div>
            </div>
        `;
        return card;
    }

    setCardStatus(el, status) {
        if (!el) {
            return;
        }
        el.textContent = this.statusLabels[status] || status;
        el.className = 'badge ' + (status === 'completed' ? 'bg-success' : status === 'failed' ? 'bg-danger' : 'bg-secondary');
    }

    renderActiveTask(card) {
        const payload = card._extractPayload;
        if (!payload) {
            return;
        }

        const activeTask = payload.activeTask || 'text';
        const toggle = card.querySelector('[data-role="task-think-toggle"]');
        if (toggle) {
            toggle.checked = activeTask === 'think';
        }

        if (activeTask === 'think') {
            if (payload.think) {
                this.renderResult(card, {
                    status: 'completed',
                    result: payload.think,
                    processing_time: payload.processingTime,
                });
                return;
            }

            this.renderResult(card, {
                status: 'failed',
                error: payload.thinkError || this.thinkErrorLabel,
            });
            return;
        }

        if (payload.text) {
            this.renderResult(card, {
                status: 'completed',
                result: payload.text,
                processing_time: payload.processingTime,
            });
            return;
        }

        this.renderResult(card, {
            status: 'failed',
            error: payload.textError || this.resultEmptyLabel,
        });
    }

    renderResult(card, data) {
        const metaEl = card.querySelector('[data-role="meta"]');
        const actionsEl = card.querySelector('[data-role="actions"]');
        const bodyEl = card.querySelector('[data-role="body"]');

        if (!bodyEl) {
            return;
        }

        metaEl?.classList.add('d-none');
        const keepActions = !!card._extractPayload;

        if (data.status === 'failed') {
            if (!keepActions) {
                actionsEl?.classList.add('d-none');
            } else {
                actionsEl?.classList.remove('d-none');
                this.updateThinkToggleVisibility(card);
            }
            bodyEl.innerHTML = `<div class="text-danger">${this.escapeHtml(data.error || this.resultEmptyLabel)}</div>`;
            return;
        }

        const result = data.result;
        const html = result?.html;
        const text = result?.text;

        if (!html && !text) {
            bodyEl.innerHTML = `<p class="text-muted mb-0">${this.escapeHtml(this.resultEmptyLabel)}</p>`;
            return;
        }

        const metaLine = this.formatMetaLine(result?.meta, data.processing_time);
        if (metaLine && metaEl) {
            metaEl.textContent = metaLine;
            metaEl.classList.remove('d-none');
        }

        actionsEl?.classList.remove('d-none');
        this.updateThinkToggleVisibility(card);
        this.updateDownloadLinks(card);

        if (html) {
            bodyEl.innerHTML = `<div class="extract-result">${html}</div>`;
            return;
        }

        bodyEl.innerHTML = `<pre class="mb-0 text-wrap" style="white-space: pre-wrap;">${this.escapeHtml(text)}</pre>`;
    }

    formatMetaLine(meta, processingTime) {
        const parts = [];

        const pages = meta?.pages_processed;
        if (pages != null && pages !== '') {
            parts.push(this.metaPagesLabel.replace(':count', String(pages)));
        }

        if (processingTime != null && processingTime > 0) {
            parts.push(this.metaDurationLabel.replace(':count', String(processingTime)));
        }

        return parts.length ? parts.join(' · ') : null;
    }

    getPlainText(result) {
        if (!result) {
            return '';
        }

        if (result.plain_text) {
            return String(result.plain_text);
        }

        if (result.html) {
            return this.htmlToPlainText(result.html);
        }

        return String(result.text || '');
    }

    htmlToPlainText(html) {
        if (!html) {
            return '';
        }

        const tmp = document.createElement('div');
        tmp.innerHTML = String(html);
        tmp.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));

        tmp.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, blockquote, pre').forEach((el) => {
            el.insertAdjacentText('beforebegin', '\n');
            el.insertAdjacentText('afterend', '\n');
        });

        return String(tmp.textContent || '')
            .replace(/\u00A0/g, ' ')
            .replace(/[ \t]{2,}/g, ' ')
            .replace(/[ \t]*\n[ \t]*/g, '\n')
            .replace(/\n{3,}/g, '\n\n')
            .trim();
    }

    updateThinkToggleVisibility(card) {
        const toggleWrap = card.querySelector('[data-role="task-think-wrap"]');
        const payload = card._extractPayload;

        if (!toggleWrap || !payload) {
            return;
        }

        if (payload.think || payload.thinkError) {
            toggleWrap.classList.remove('d-none');
            return;
        }

        toggleWrap.classList.add('d-none');
    }

    updateDownloadLinks(card) {
        const extractionId = card.dataset.extractionId;
        const activeTask = card._extractPayload?.activeTask || 'text';

        if (!extractionId) {
            return;
        }

        const txtLink = card.querySelector('[data-role="download-txt"]');
        const mdLink = card.querySelector('[data-role="download-md"]');

        if (txtLink) {
            txtLink.href = ExtractApi.downloadUrl(extractionId, 'txt', activeTask);
        }

        if (mdLink) {
            mdLink.href = ExtractApi.downloadUrl(extractionId, 'md', activeTask);
        }
    }

    async copyResult(btn, card) {
        const payload = card._extractPayload;
        const activeTask = payload?.activeTask || 'text';
        const result = activeTask === 'think' ? payload?.think : payload?.text;
        const text = this.getPlainText(result);

        if (!text) {
            return;
        }

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.left = '-9999px';
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }

            const originalLabel = btn.textContent;
            btn.textContent = this.copyDoneLabel;
            setTimeout(() => {
                btn.textContent = originalLabel;
            }, 2000);
        } catch (e) {
            console.error('Copy failed:', e);
        }
    }

    formatErrors(errors) {
        if (typeof errors === 'string') {
            return errors;
        }
        if (Array.isArray(errors)) {
            return errors.join(', ');
        }
        if (errors && typeof errors === 'object') {
            return Object.values(errors).flat().join(', ');
        }
        return 'Ошибка загрузки';
    }

    escapeHtml(value) {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    setLoading(loading) {
        this.isProcessing = loading;
        const spinner = this.submitBtn?.element?.querySelector('.spinner-border');
        spinner?.classList.toggle('d-none', !loading);
        this.submitBtn.element.disabled = loading || this.pendingFiles.length === 0;
    }

    sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
}
